import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { Voice } from "../types/voice";
import Cookies from "js-cookie";
import WaveSurfer from "wavesurfer.js";

interface VoiceContextType {
  currentVoice: Voice | null;
  playingUuid: string | null;
  loadingUuid: string | null;
  isPlaying: boolean;
  isMuted: boolean;
  waveformRef: React.RefObject<HTMLDivElement>;
  wavesurferInstance: WaveSurfer | null;
  toggleAudio: (uuid: string) => Promise<void>;
  downloadVoice: (uuid: string, filename: string) => Promise<void>;
  seekTo: (position: number) => void;
  toggleMute: () => void;
  previousVoice: () => void;
  nextVoice: () => void;
  audioCache: { [key: string]: string };
  voices: Voice[];
  setVoices: (voices: Voice[]) => void;
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined);

export const useVoiceContext = () => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error("useVoiceContext must be used within a VoiceProvider");
  }
  return context;
};

interface VoiceProviderProps {
  children: ReactNode;
}

export const VoiceProvider = ({ children }: VoiceProviderProps) => {
  // 基本状態
  const [currentVoice, setCurrentVoice] = useState<Voice | null>(null);
  const [playingUuid, setPlayingUuid] = useState<string | null>(null);
  const [loadingUuid, setLoadingUuid] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [audioCache, setAudioCache] = useState<{ [key: string]: string }>({});
  const [voices, setVoices] = useState<Voice[]>([]);

  // Wavesurfer関連
  const [wavesurferInstance, setWavesurferInstance] = useState<WaveSurfer | null>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const isProcessing = useRef(false); // 処理中フラグ（再生・停止の重複防止）

  const csrftoken = Cookies.get("csrftoken");
  const headers = new Headers({
    "Content-Type": "application/json",
    "X-CSRFToken": csrftoken!,
  });

  // Wavesurferの作成（シンプル化）
  const createWavesurfer = useCallback(
    (container: HTMLElement, url: string) => {
      // 既存のインスタンスがあれば破棄
      if (wavesurferInstance) {
        try {
          wavesurferInstance.destroy();
        } catch (e) {
          console.error("Error destroying wavesurfer:", e);
        }
      }

      // 新しいインスタンスを作成
      const wavesurfer = WaveSurfer.create({
        container,
        waveColor: "#4f4f4f",
        progressColor: "#1976d2",
        cursorColor: "#1976d2",
        height: 40, // 波形の高さを50pxに変更
        barWidth: 2,
        barGap: 1,
        barRadius: 3,
        normalize: true, // 波形を正規化
        url,
      });

      // 基本的なイベントハンドラ
      wavesurfer.on("ready", () => {
        wavesurfer.play();
        setIsPlaying(true);
      });

      wavesurfer.on("finish", () => {
        setIsPlaying(false);
        setPlayingUuid(null);
        // 再生終了後も音声情報を保持するためsetCurrentVoice(null)を削除
      });

      setWavesurferInstance(wavesurfer);
      return wavesurfer;
    },
    [wavesurferInstance]
  );

  // 音声再生の切り替え
  const toggleAudio = useCallback(
    async (uuid: string) => {
      // 処理中は操作を無視
      if (isProcessing.current) return;
      isProcessing.current = true;

      try {
        if (playingUuid === uuid) {
          // 同じ音声の再生/停止を切り替え
          if (wavesurferInstance) {
            if (isPlaying) {
              // 再生中なら停止
              wavesurferInstance.pause();
              setIsPlaying(false);
            } else {
              // 停止中なら再開
              wavesurferInstance.play();
              setIsPlaying(true);
            }
          }
        } else {
          // 新しい音声を再生
          setLoadingUuid(uuid);

          // 現在再生中の音声があれば停止
          if (wavesurferInstance && isPlaying) {
            wavesurferInstance.pause();
          }

          // 音声URLを取得
          let url = audioCache[uuid];
          if (!url) {
            try {
              const response = await fetch(`/api/v1/voice-generator/download/${uuid}`, {
                method: "GET",
                headers,
              });
              const blob = await response.blob();
              url = window.URL.createObjectURL(blob);

              // キャッシュに保存
              setAudioCache((prev) => ({
                ...prev,
                [uuid]: url,
              }));
            } catch (e) {
              console.error("Error fetching audio:", e);
              setLoadingUuid(null);
              isProcessing.current = false;
              return;
            }
          }

          // 現在の音声を更新
          const voice = voices.find((v) => v.uuid === uuid);
          if (voice) {
            setCurrentVoice(voice);
          }

          // 波形を作成して再生
          if (waveformRef.current) {
            createWavesurfer(waveformRef.current, url);
          }

          setPlayingUuid(uuid);
          setLoadingUuid(null);
        }
      } catch (e) {
        console.error("Error toggling audio:", e);
      } finally {
        isProcessing.current = false;
      }
    },
    [playingUuid, wavesurferInstance, isPlaying, audioCache, voices, headers, createWavesurfer]
  );

  // 音声をダウンロード
  const downloadVoice = useCallback(
    async (uuid: string, filename: string) => {
      try {
        const response = await fetch(`/api/v1/voice-generator/download/${uuid}`, {
          method: "GET",
          headers,
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Failed to download voice:", error);
      }
    },
    [headers]
  );

  // ミュート切り替え
  const toggleMute = useCallback(() => {
    if (wavesurferInstance) {
      wavesurferInstance.setMuted(!isMuted);
      setIsMuted(!isMuted);
    }
  }, [wavesurferInstance, isMuted]);

  // 特定位置から再生
  const seekTo = useCallback(
    (position: number) => {
      if (!wavesurferInstance || isProcessing.current) return;

      try {
        isProcessing.current = true;
        wavesurferInstance.seekTo(position);
        isProcessing.current = false;
      } catch (e) {
        console.error("Error seeking:", e);
        isProcessing.current = false;
      }
    },
    [wavesurferInstance]
  );

  // 前の音声へ
  const previousVoice = useCallback(() => {
    if (!playingUuid || voices.length === 0 || isProcessing.current) return;

    const currentIndex = voices.findIndex((voice) => voice.uuid === playingUuid);
    if (currentIndex > 0) {
      toggleAudio(voices[currentIndex - 1].uuid);
    }
  }, [playingUuid, voices, toggleAudio]);

  // 次の音声へ
  const nextVoice = useCallback(() => {
    if (!playingUuid || voices.length === 0 || isProcessing.current) return;

    const currentIndex = voices.findIndex((voice) => voice.uuid === playingUuid);
    if (currentIndex < voices.length - 1) {
      toggleAudio(voices[currentIndex + 1].uuid);
    }
  }, [playingUuid, voices, toggleAudio]);

  // アンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (wavesurferInstance) {
        try {
          wavesurferInstance.destroy();
        } catch (e) {
          console.error("Error destroying wavesurfer on unmount:", e);
        }
      }
    };
  }, [wavesurferInstance]);

  const value = {
    currentVoice,
    playingUuid,
    loadingUuid,
    isPlaying,
    isMuted,
    waveformRef,
    wavesurferInstance,
    toggleAudio,
    downloadVoice,
    seekTo,
    toggleMute,
    previousVoice,
    nextVoice,
    audioCache,
    voices,
    setVoices,
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};
